import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from 'recharts';

const CHART_COLORS = ['#1B2A4A', '#D9480F', '#F59E0B', '#16A34A', '#0284C7'];

export default function AnalyticsTab({ analytics }) {
  if (!analytics) {
    return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" /></div>;
  }

  const scoreDistData = analytics.score_distribution
    ? Object.entries(analytics.score_distribution).map(([range, count]) => ({ range, count }))
    : [];

  const moduleChartData = analytics.module_stats?.map(m => ({
    name: m.title.length > 20 ? m.title.slice(0, 20) + '...' : m.title,
    Completed: m.completed,
    'In Progress': m.in_progress
  })) || [];

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'Test Pass Rate', value: `${analytics.pass_rate}%` },
          { label: 'Total Test Attempts', value: analytics.total_test_attempts },
          { label: 'Tests This Week', value: analytics.recent_activity?.tests || 0 },
        ].map((stat) => (
          <Card key={stat.label} className="bg-white rounded-2xl border border-stone-200 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1B2A4A]">{stat.value}</p>
              <p className="text-xs text-stone-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {moduleChartData.length > 0 && (
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="module-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Module Completion</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={moduleChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#78716C' }} />
                <YAxis tick={{ fontSize: 11, fill: '#78716C' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                <Bar dataKey="Completed" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="In Progress" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {scoreDistData.length > 0 && (
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="score-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Test Score Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie data={scoreDistData} dataKey="count" nameKey="range" cx="50%" cy="50%" outerRadius={80} label={({ range, count }) => count > 0 ? `${range}: ${count}` : ''}>
                  {scoreDistData.map((entry, idx) => <Cell key={entry.range} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {analytics.top_performers?.length > 0 && (
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="top-performers">
          <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Top Performers</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.top_performers.slice(0, 5).map((p, i) => (
                <div key={p.name} className="flex items-center justify-between p-2 rounded-xl bg-stone-50">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-white bg-[#1B2A4A] rounded-full">{i + 1}</span>
                    <span className="text-sm font-medium text-stone-900">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span>{p.avg_score}% avg</span>
                    <span>{p.modules_done} modules</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
